"use client";

import { useEffect } from "react";
import { cn } from "@dionatanbig2be/big2be-ui";
import { USER_COOKIE } from "@/config/cookies";
import { readClientCookie } from "@/shared/helpers/cookies";
import { AuthFlowBody, AuthFlowTitle, readSafeRedirect, useAuthModal } from "@/modules/auth";
import { MediaImage } from "@/shared/components";
import { useHeaderData } from "../hooks/useHeaderData";

/**
 * Loja fechada (login_form_fields.requireLogin): o root layout monta isto NO
 * LUGAR do AppShell para quem não tem sessão, em qualquer rota.
 *
 * Por trás fica só uma CASCA da loja — cores e logo do tema, com blocos
 * esqueleto no lugar do conteúdo. O desfoque é só visual: a proteção é que
 * nenhum dado real (produto, preço, categoria) é renderizado, então remover o
 * blur pelo DevTools não revela nada. A página de verdade (`children`) nem é
 * montada pelo layout.
 *
 * Por cima, o fluxo do AuthModal fixo e sem saída. Não usa o Modal da lib de
 * propósito: ele é um `<dialog>` nativo, que o Esc fecha por fora do React.
 * Uma camada própria não tem como ser dispensada.
 *
 * No sucesso, recarrega de verdade em vez de navegar: o gate é decidido no SSR
 * pela presença do cookie de sessão, e só um request novo re-renderiza o
 * layout já com ele.
 *
 * ── Cache de HTML na borda ────────────────────────────────────────────────
 * O Cloudflare de alguns tenants cacheia o HTML ignorando o `no-store` da
 * origem e SEM bypass por cookie de sessão. Resultado: quem acabou de logar
 * recarrega e recebe a cópia do gate que um anônimo deixou na borda — o login
 * "dá 200 e pede login de novo". A query string entra na chave do cache,
 * então recarregar com uma query única força o request a chegar ao servidor.
 * O conserto de verdade é a regra de bypass no Cloudflare; isto aqui garante
 * que o login funcione enquanto ela não existir.
 */
const CACHE_BUST_PARAM = "_s";
const CACHE_BUST_GUARD = "login-gate-cache-bust";
/** Uma tentativa por janela: se a origem também devolver o gate, a sessão é que morreu. */
const CACHE_BUST_WINDOW_MS = 30_000;

function withCacheBust(target: string): string {
    const url = new URL(target, window.location.origin);
    url.searchParams.set(CACHE_BUST_PARAM, Date.now().toString(36));
    return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Tem sessão (cookie de identidade legível) mas recebeu o gate: é HTML velho
 * da borda. Recarrega uma vez furando o cache; o guard evita loop quando o
 * cookie de identidade sobrou de uma sessão que de fato expirou.
 */
function useEscapeStaleEdgeCache() {
    useEffect(() => {
        if (!readClientCookie(USER_COOKIE)) return;
        try {
            const last = Number(window.sessionStorage.getItem(CACHE_BUST_GUARD) ?? 0);
            if (Date.now() - last < CACHE_BUST_WINDOW_MS) return;
            window.sessionStorage.setItem(CACHE_BUST_GUARD, String(Date.now()));
        } catch {
            return;
        }
        window.location.replace(withCacheBust(window.location.href));
    }, []);
}

export function LoginGate() {
    const view = useAuthModal((s) => s.view);
    useEscapeStaleEdgeCache();

    function handleSuccess() {
        window.location.replace(withCacheBust(readSafeRedirect() ?? window.location.href));
    }

    return (
        <>
            <StoreShellSkeleton />
            <div className="fixed inset-0 z-50 flex items-end justify-center bg-base-content/30 backdrop-blur-md md:items-center md:p-4">
                <section
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="login-gate-title"
                    className={cn(
                        "flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl border border-base-content/10 bg-base-100 shadow-2xl supports-[height:100dvh]:max-h-[90dvh] md:rounded-2xl",
                        // Cadastro tem grid de 2 colunas — mesma largura do AuthModal.
                        view === "register" ? "md:max-w-2xl" : "md:max-w-md"
                    )}
                >
                    <h2
                        id="login-gate-title"
                        className="m-0 px-5 pt-5 pb-3 text-base font-bold leading-none"
                    >
                        <AuthFlowTitle />
                    </h2>
                    <div className="overflow-y-auto px-5 pb-5">
                        <AuthFlowBody onSuccess={handleSuccess} />
                    </div>
                </section>
            </div>
        </>
    );
}

const SKELETON_PRODUCTS = Array.from({ length: 10 }, (_, index) => index);

/**
 * Silhueta da vitrine (header, faixa de categorias, banner, grade de produtos).
 * `inert` tira tudo do foco e do leitor de tela — atrás do gate não há nada
 * para alcançar.
 */
function StoreShellSkeleton() {
    const { logoPath, logoAlt } = useHeaderData();

    return (
        <div inert aria-hidden="true" className="flex h-screen flex-col overflow-hidden blur-sm supports-[height:100dvh]:h-dvh">
            <header className="bg-primary text-primary-content">
                <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4">
                    {logoPath ? (
                        <MediaImage
                            path={logoPath}
                            alt={logoAlt}
                            intrinsic
                            className="h-9 w-auto object-contain"
                        />
                    ) : (
                        <div className="h-8 w-28 rounded-field bg-current/20" />
                    )}
                    <div className="hidden h-10 flex-1 rounded-field bg-current/15 md:block" />
                    <div className="ml-auto flex gap-3">
                        <div className="h-9 w-9 rounded-full bg-current/20" />
                        <div className="h-9 w-9 rounded-full bg-current/20" />
                    </div>
                </div>
            </header>
            <div className="flex-1 overflow-hidden bg-base-100">
                <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6">
                    <div className="flex gap-3 overflow-hidden">
                        {SKELETON_PRODUCTS.slice(0, 6).map((index) => (
                            <div
                                key={index}
                                className="h-6 w-24 shrink-0 rounded-field bg-base-300"
                            />
                        ))}
                    </div>
                    <div className="aspect-[3/1] w-full rounded-box bg-base-300" />
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                        {SKELETON_PRODUCTS.map((index) => (
                            <div key={index} className="flex flex-col gap-2">
                                <div className="aspect-square w-full rounded-box bg-base-300" />
                                <div className="h-3 w-4/5 rounded-field bg-base-300" />
                                <div className="h-4 w-1/2 rounded-field bg-base-300" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
